import React, { useState, useCallback, useEffect } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import {
  Loader2, ChevronRight, ChevronLeft, Search, AlertTriangle,
  CheckCircle, XCircle, ArrowRightLeft, Plus, Minus, RotateCcw, Eye
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GROUP_ICONS = {
  Ressources: '🚛', Operations: '📦', Utilisateurs: '👤', Configuration: '⚙️', Autre: '📁'
};

const STEPS = ['preview', 'compare', 'restore', 'done'];
const STEP_LABELS = ['Selection', 'Comparaison', 'Restauration', 'Termine'];

export default function SelectiveRestoreModal({ backup, onClose, onComplete }) {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState({});
  const [comparison, setComparison] = useState(null);
  const [restoreOptions, setRestoreOptions] = useState({});
  const [result, setResult] = useState(null);
  const headers = { Authorization: 'Bearer cookie-auth', 'Content-Type': 'application/json' };

  const fetchPreview = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/backups/${backup.id}/preview`, { headers });
      setPreview(res.data);
    } catch { setPreview(null); }
    setLoading(false);
  }, [backup.id]);

  useEffect(() => { fetchPreview(); }, [fetchPreview]);

  const toggleCollection = (name) => {
    setSelected(prev => ({ ...prev, [name]: !prev[name] }));
  };

  const toggleGroup = (groupColls) => {
    const allSelected = groupColls.every(c => selected[c.name]);
    const update = {};
    groupColls.forEach(c => { update[c.name] = !allSelected; });
    setSelected(prev => ({ ...prev, ...update }));
  };

  const selectedCount = Object.values(selected).filter(Boolean).length;

  const handleCompare = async () => {
    const colls = Object.entries(selected).filter(([_, v]) => v).map(([k]) => k);
    if (colls.length === 0) return;
    setLoading(true);
    setStep(1);
    try {
      const res = await axios.post(
        `${API}/backups/${backup.id}/compare`,
        { collections: colls },
        { headers }
      );
      setComparison(res.data.results);
      const opts = {};
      Object.entries(res.data.results).forEach(([name, r]) => {
        opts[name] = { missing: r.missing > 0, modified: r.modified > 0 };
      });
      setRestoreOptions(opts);
    } catch { setComparison(null); }
    setLoading(false);
  };

  const handleRestore = async () => {
    const payload = {};
    Object.entries(restoreOptions).forEach(([name, opts]) => {
      if (opts.missing || opts.modified) payload[name] = opts;
    });
    if (Object.keys(payload).length === 0) return;
    setLoading(true);
    setStep(2);
    try {
      const res = await axios.post(
        `${API}/backups/${backup.id}/selective-restore`,
        { collections: payload },
        { headers }
      );
      setResult(res.data);
      setStep(3);
    } catch (e) {
      setResult({ error: e.response?.data?.detail || 'Erreur lors de la restauration' });
      setStep(3);
    }
    setLoading(false);
  };

  const totalToRestore = comparison
    ? Object.entries(restoreOptions).reduce((sum, [name, opts]) => {
        const r = comparison[name];
        if (!r) return sum;
        return sum + (opts.missing ? r.missing : 0) + (opts.modified ? r.modified : 0);
      }, 0)
    : 0;

  // Group collections for display
  const groupedCollections = {};
  if (preview) {
    preview.collections.forEach(c => {
      const g = c.group || 'Autre';
      if (!groupedCollections[g]) groupedCollections[g] = [];
      groupedCollections[g].push(c);
    });
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="selective-restore-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-bold text-gray-900">Restauration selective</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-lg" data-testid="close-modal-btn">&times;</button>
          </div>
          {/* Steps */}
          <div className="flex items-center gap-2">
            {STEP_LABELS.map((label, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold
                  ${i <= step ? 'bg-gray-900 text-white' : 'bg-gray-200 text-gray-400'}`}>
                  {i + 1}
                </div>
                <span className={`text-xs ${i <= step ? 'text-gray-900 font-medium' : 'text-gray-400'}`}>{label}</span>
                {i < 3 && <ChevronRight className="w-3 h-3 text-gray-300" />}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading && (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              <span className="ml-2 text-sm text-gray-500">
                {step === 0 ? 'Lecture de l\'archive...' : step === 1 ? 'Analyse des differences...' : 'Restauration en cours...'}
              </span>
            </div>
          )}

          {/* STEP 0: Preview + Selection */}
          {step === 0 && !loading && preview && (
            <div className="space-y-4">
              <p className="text-xs text-gray-500">Sélectionnez les collections a comparer avec la base actuelle.</p>
              {Object.entries(groupedCollections).map(([group, colls]) => (
                <div key={group}>
                  <button
                    className="flex items-center gap-2 mb-1.5 text-xs font-semibold text-gray-600 hover:text-gray-900"
                    onClick={() => toggleGroup(colls)}
                  >
                    <span>{GROUP_ICONS[group] || '📁'}</span>
                    <span>{group}</span>
                    <Badge variant="outline" className="text-[9px]">{colls.length}</Badge>
                  </button>
                  <div className="space-y-1 ml-6">
                    {colls.map(c => (
                      <label
                        key={c.name}
                        className={`flex items-center gap-3 py-1.5 px-2 rounded cursor-pointer hover:bg-gray-50
                          ${selected[c.name] ? 'bg-blue-50 border border-blue-200' : 'border border-transparent'}`}
                      >
                        <Checkbox
                          checked={!!selected[c.name]}
                          onCheckedChange={() => toggleCollection(c.name)}
                          data-testid={`select-${c.name}`}
                        />
                        <span className="text-sm text-gray-800 flex-1">{c.label}</span>
                        <span className="text-xs text-gray-400">{c.count} docs</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* STEP 1: Comparison Results */}
          {step === 1 && !loading && comparison && (
            <div className="space-y-4">
              <p className="text-xs text-gray-500">Cochez les categories a restaurer pour chaque collection.</p>
              {Object.entries(comparison).map(([name, r]) => {
                const opts = restoreOptions[name] || {};
                const hasDelta = r.missing > 0 || r.modified > 0;
                return (
                  <div key={name} className={`border rounded-lg p-3 ${hasDelta ? 'border-gray-200' : 'border-gray-100 opacity-60'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-gray-800">{r.label}</span>
                      <span className="text-[10px] text-gray-400">backup: {r.backupCount} vs actuel: {r.currentCount}</span>
                    </div>
                    <div className="grid grid-cols-4 gap-2 text-xs">
                      {/* Missing */}
                      <label className={`flex items-center gap-2 p-2 rounded border cursor-pointer
                        ${r.missing > 0 ? (opts.missing ? 'bg-red-50 border-red-200' : 'border-gray-200 hover:bg-gray-50') : 'border-gray-100 opacity-40 cursor-default'}`}>
                        <Checkbox
                          checked={!!opts.missing}
                          disabled={r.missing === 0}
                          onCheckedChange={(v) => setRestoreOptions(prev => ({
                            ...prev, [name]: { ...prev[name], missing: !!v }
                          }))}
                          data-testid={`restore-missing-${name}`}
                        />
                        <div>
                          <div className="flex items-center gap-1">
                            <Minus className="w-3 h-3 text-red-500" />
                            <span className="font-bold text-red-600">{r.missing}</span>
                          </div>
                          <span className="text-gray-500">manquants</span>
                        </div>
                      </label>

                      {/* Modified */}
                      <label className={`flex items-center gap-2 p-2 rounded border cursor-pointer
                        ${r.modified > 0 ? (opts.modified ? 'bg-amber-50 border-amber-200' : 'border-gray-200 hover:bg-gray-50') : 'border-gray-100 opacity-40 cursor-default'}`}>
                        <Checkbox
                          checked={!!opts.modified}
                          disabled={r.modified === 0}
                          onCheckedChange={(v) => setRestoreOptions(prev => ({
                            ...prev, [name]: { ...prev[name], modified: !!v }
                          }))}
                          data-testid={`restore-modified-${name}`}
                        />
                        <div>
                          <div className="flex items-center gap-1">
                            <ArrowRightLeft className="w-3 h-3 text-amber-500" />
                            <span className="font-bold text-amber-600">{r.modified}</span>
                          </div>
                          <span className="text-gray-500">modifies</span>
                        </div>
                      </label>

                      {/* Identical */}
                      <div className="flex items-center gap-2 p-2 rounded border border-gray-100 opacity-60">
                        <div>
                          <div className="flex items-center gap-1">
                            <CheckCircle className="w-3 h-3 text-emerald-500" />
                            <span className="font-bold text-emerald-600">{r.identical}</span>
                          </div>
                          <span className="text-gray-500">identiques</span>
                        </div>
                      </div>

                      {/* New */}
                      <div className="flex items-center gap-2 p-2 rounded border border-gray-100 opacity-60">
                        <div>
                          <div className="flex items-center gap-1">
                            <Plus className="w-3 h-3 text-blue-500" />
                            <span className="font-bold text-blue-600">{r.new}</span>
                          </div>
                          <span className="text-gray-500">nouveaux</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* STEP 2: Restoring... (handled by loading state) */}

          {/* STEP 3: Results */}
          {step === 3 && !loading && result && (
            <div className="space-y-4">
              {result.error ? (
                <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <XCircle className="w-6 h-6 text-red-500 shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-red-800">Erreur</p>
                    <p className="text-xs text-red-600">{result.error}</p>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <CheckCircle className="w-6 h-6 text-emerald-500 shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-emerald-800">Restauration terminée</p>
                      <p className="text-xs text-emerald-600">
                        {result.total_inserted} insere(s) + {result.total_updated} mis a jour
                      </p>
                    </div>
                  </div>
                  {result.log?.map((entry, i) => (
                    <div key={i} className="flex items-center justify-between py-2 px-3 border border-gray-100 rounded text-xs">
                      <span className="font-medium text-gray-700">{entry.label}</span>
                      <div className="flex gap-3">
                        {entry.inserted > 0 && (
                          <span className="text-emerald-600">+{entry.inserted} inseres</span>
                        )}
                        {entry.updated > 0 && (
                          <span className="text-amber-600">{entry.updated} mis a jour</span>
                        )}
                        {entry.inserted === 0 && entry.updated === 0 && (
                          <span className="text-gray-400">aucun changement</span>
                        )}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-100 flex items-center justify-between">
          <div className="text-xs text-gray-400">
            {step === 0 && selectedCount > 0 && `${selectedCount} collection(s) selectionnee(s)`}
            {step === 1 && totalToRestore > 0 && (
              <span className="text-amber-600 font-medium flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5" />
                {totalToRestore} document(s) seront restaures
              </span>
            )}
          </div>
          <div className="flex gap-2">
            {step === 3 ? (
              <Button onClick={() => { onComplete?.(); onClose(); }} data-testid="close-done-btn">
                Fermer
              </Button>
            ) : (
              <>
                {step > 0 && step < 3 && (
                  <Button variant="outline" size="sm" onClick={() => setStep(s => s - 1)} data-testid="back-btn">
                    <ChevronLeft className="w-4 h-4 mr-1" /> Retour
                  </Button>
                )}
                {step === 0 && (
                  <Button size="sm" onClick={handleCompare} disabled={selectedCount === 0} data-testid="compare-btn">
                    <Search className="w-4 h-4 mr-1" /> Comparer ({selectedCount})
                  </Button>
                )}
                {step === 1 && (
                  <Button
                    size="sm"
                    onClick={handleRestore}
                    disabled={totalToRestore === 0}
                    className="bg-amber-600 hover:bg-amber-700"
                    data-testid="restore-btn"
                  >
                    <RotateCcw className="w-4 h-4 mr-1" /> Restaurer le delta ({totalToRestore})
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
